/**
 * Web Vitals Performance Monitoring
 * Tracks Core Web Vitals metrics and sends them to analytics
 */

import { getCLS, getFID, getFCP, getLCP, getTTFB, type Metric } from "web-vitals";
import { logger } from "./logger";

interface PerformanceMetric extends Metric {
  timestamp: number;
  url: string;
  userAgent: string;
}

/**
 * Send performance metrics to analytics service
 */
const sendToAnalytics = (metric: Metric) => {
  const enhancedMetric: PerformanceMetric = {
    ...metric,
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  // Log to logger in development
  if (process.env.NODE_ENV === "development") {
    logger.info(`Web Vitals ${metric.name}: ${metric.value.toFixed(2)}ms`, {
      component: "WebVitals",
      metric: metric.name,
      value: metric.value,
      id: metric.id,
    });
  }

  // Send to PostHog if available
  if (typeof window !== "undefined" && (window as any).posthog) {
    (window as any).posthog.capture("web-vitals", {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_id: metric.id,
      metric_delta: metric.delta,
      page_url: enhancedMetric.url,
    });
  }

  // You can also send to other analytics services here
  // Example: Google Analytics
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", metric.name, {
      value: Math.round(metric.value),
      metric_id: metric.id,
      non_interaction: true,
    });
  }
};

/**
 * Initialize Web Vitals monitoring
 */
export const initWebVitals = () => {
  // Cumulative Layout Shift
  getCLS(sendToAnalytics);

  // First Input Delay
  getFID(sendToAnalytics);

  // First Contentful Paint
  getFCP(sendToAnalytics);

  // Largest Contentful Paint
  getLCP(sendToAnalytics);

  // Time to First Byte
  getTTFB(sendToAnalytics);
};

/**
 * Get current performance metrics snapshot
 */
export const getPerformanceSnapshot = () => {
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;

  if (!navigation) return null;

  return {
    // Navigation timing
    dns: navigation.domainLookupEnd - navigation.domainLookupStart,
    tcp: navigation.connectEnd - navigation.connectStart,
    ttfb: navigation.responseStart - navigation.requestStart,
    download: navigation.responseEnd - navigation.responseStart,
    domInteractive: navigation.domInteractive - navigation.fetchStart,
    domComplete: navigation.domComplete - navigation.fetchStart,
    loadComplete: navigation.loadEventEnd - navigation.fetchStart,

    // Resource timing
    resources: performance.getEntriesByType("resource").length,

    // Memory usage (if available)
    memory: (performance as any).memory
      ? {
          usedJSHeapSize: Math.round((performance as any).memory.usedJSHeapSize / 1048576),
          totalJSHeapSize: Math.round((performance as any).memory.totalJSHeapSize / 1048576),
          limit: Math.round((performance as any).memory.jsHeapSizeLimit / 1048576),
        }
      : null,
  };
};

/**
 * Custom performance mark for measuring specific operations
 */
export const measurePerformance = (name: string, fn: () => any) => {
  const startMark = `${name}-start`;
  const endMark = `${name}-end`;
  const measureName = `${name}-duration`;

  performance.mark(startMark);
  const result = fn();
  performance.mark(endMark);

  performance.measure(measureName, startMark, endMark);
  const measure = performance.getEntriesByName(measureName)[0];

  if (measure && measure.duration > 16) {
    // Longer than one frame (60fps)
    logger.performance(name, measure.duration, { operation: name });
  }

  // Clean up marks and measures
  performance.clearMarks(startMark);
  performance.clearMarks(endMark);
  performance.clearMeasures(measureName);

  return result;
};
